import { refreshAccessToken } from '../Redux/Company/companysApi';
import { handleAuthFailure } from '../Redux/Auth/authApi';

interface RequestConfig extends RequestInit {
  maxRetries?: number;
  requiresAuth?: boolean;
  retryOnError?: boolean;  // HTTPエラー時のリトライを制御（デフォルト: false）
}

/**
 * HTTPエラー（!response.ok）時に投げる Error。
 * message には従来どおりレスポンス本文（BEは {status,message,...} のJSON文字列）を入れるため、
 * 既存の extractApiMessage / .message 参照はそのまま動く（後方互換）。
 * 加えて status（HTTPステータス）と parsedBody（本文をJSONパースできた場合のオブジェクト）を保持し、
 * 呼び出し側が 409 などのステータスや affectedCount 等の付随情報を判定できるようにする。
 */
export class ApiError extends Error {
  status: number;
  parsedBody: unknown;

  constructor(status: number, bodyText: string) {
    super(bodyText || `HTTP error! status: ${status}`);
    this.name = 'ApiError';
    this.status = status;
    let parsed: unknown = null;
    try {
      parsed = bodyText ? JSON.parse(bodyText) : null;
    } catch {
      parsed = null;
    }
    this.parsedBody = parsed;
    // TypeScript(ES5ターゲット)での instanceof 破綻を防ぐ
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * API呼び出しを行う共通関数
 * @param url APIエンドポイント
 * @param config リクエスト設定
 * @returns レスポンスデータ
 */
export async function fetchWithRetry<T>(
  url: string,
  config: RequestConfig = {}
): Promise<T> {
  const {
    maxRetries = 3,
    requiresAuth = true,
    retryOnError = false,  // デフォルトではHTTPエラーでリトライしない
    headers: initialHeaders = {},
    method = 'GET',
    ...restConfig
  } = config;

  let currentTry = 0;
  let accessToken = localStorage.getItem('accessToken');

  while (currentTry < maxRetries) {
    try {
      const headers: Record<string, string> = {
        ...initialHeaders as Record<string, string>,
      };

      if (requiresAuth) {
        if (!accessToken) {
          // トークンが無い＝未認証。セッションを破棄してログインへ誘導する。
          handleAuthFailure();
          throw new Error('Unauthorized: No access token found.');
        }
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(url, {
        method,
        ...restConfig,
        headers,
      });

      // 認証エラーの場合
      if (response.status === 401 && requiresAuth) {
        try {
          accessToken = await refreshAccessToken();
          currentTry++;
          continue; // リトライ
        } catch (refreshError) {
          // リフレッシュトークンも失効＝再認証が必要。セッションを破棄してログインへ誘導する。
          handleAuthFailure();
          throw new Error('Failed to refresh access token');
        }
      }

      // HTTPエラーの場合
      if (!response.ok) {
        const errorText = await response.text();
        const error = new ApiError(response.status, errorText);

        // retryOnErrorがfalseの場合は即座にthrow（リトライしない）
        if (!retryOnError) {
          throw error;
        }

        // retryOnErrorがtrueで、まだリトライ回数が残っている場合
        if (currentTry < maxRetries - 1) {
          currentTry++;
          console.error(`API call failed (attempt ${currentTry}/${maxRetries}):`, error);
          continue;
        }

        throw error;
      }

      // 204 / 205 No Content の場合は常にnullを返す
      if (response.status === 204 || response.status === 205) {
        return null as T;
      }

      // レスポンスボディがある場合はJSONとして解析
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }

      // 成功レスポンスだが本文が無い/非JSONの場合（例: Mono<Void> の 200 空ボディ）
      // 本文が空ならnullを返し、非空のJSON以外が来た場合のみエラーとする
      const text = await response.text();
      if (!text) {
        return null as T;
      }

      // JSONレスポンスが期待されるがContent-Typeが異なる場合
      throw new Error('Expected JSON response but received different content type');
    } catch (error) {
      // ネットワークエラーやその他の予期しない例外の場合
      // retryOnErrorの設定に関わらず、最後の試行でなければリトライ
      if (currentTry === maxRetries - 1 || !retryOnError) {
        throw error;
      }
      currentTry++;
      console.error(`API call failed (attempt ${currentTry}/${maxRetries}):`, error);
    }
  }

  throw new Error('Maximum retry attempts reached');
}

/**
 * APIリクエストを作成するヘルパー関数
 */
export function createApiRequest<T>(endpoint: string, config: RequestConfig = {}) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const url = `${baseUrl}${endpoint}`;
  return fetchWithRetry<T>(url, config);
}
